import { Component, OnInit, ViewChild } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { Actions, ofType } from '@ngrx/effects'
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap'
import { NgxPermissionsService, NgxRolesService } from 'ngx-permissions'
import { NgPerfume } from 'perfume.js/angular'
import { debounceTime, distinctUntilChanged, filter, take, tap, map, switchMap, startWith } from 'rxjs/operators'
import * as lodash from 'lodash'
import * as moment from 'moment'
import {
  CalendarComponent,
  CalendarConfig,
  CalendarEventTimeRange,
  CalendarSelectionInfo,
  CalendarViewPortInfo,
} from '@tokeet/ngx-multi-event-calendar'

import { FilterGroup, FiltersService } from '@tv3/services/utils/filters.service'
import * as fromRoot from '@tv3/store/state'
import {
  AddHoldEventComplete,
  DeleteHoldEventComplete,
  LoadCalendarRange,
  LoadCalendarRangeComplete,
  UpdateHoldEventComplete,
} from '@tv3/store/calendar/calendar.actions'
import {
  selectCalendarLoading,
  selectDoubleBookedCalendarEvents,
  selectMultiCalendarEventsViews,
} from '@tv3/store/calendar/calendar.selectors'
import { MultiCalendarEventView } from '@tv3/store/calendar/calendar.view'
import {
  DateRangePickerSelection,
  DefaultRentalColor,
  Destroyable,
  isSomething,
  localTodayToUTC,
  Rental,
  selectAllRentals,
  selectRentalsLoaded,
  itemsInTimeRange,
  toMoment,
  untilDestroy,
  updateRentalComplete,
  updateRentalBaseRateComplete,
  asLocalDate,
  loadRentals,
  TOKEET_CHANNEL_ID,
  selectRentalSubUnits,
  selectAllRentalSubUnits,
  selectRentalMultiUnitGroups,
  selectRentalEntities,
  isSubunit,
  isMainUnit,
} from '@tokeet-frontend/tv3-platform'
import { MultiCalendarAvailabilityTypes } from '@tv3/interfaces/filters/calendar.filters'
import { ActionDialogService } from '@tv3/shared/dialogs/action-dialog/action-dialog.service'
import {
  CancelBookingComplete,
  ChangeBookingDetailsComplete,
  ChangeInquiryArriveComplete,
  ChangeInquiryDepartComplete,
  ChangeInquiryGuestComplete,
  ConfirmBookingComplete,
} from '@tv3/store/inquiry/inquiry-fields.actions'
import { OpenRentalOverlay } from '@tv3/store/overlay/overlay.actions'
import { InquirySharedDialogsService } from '@tv3/containers/inquiries/dialogs/inquiry-shared-dialogs.service'
import { RentalDialogTab } from '@tv3/containers/rentals/overlays/rental-overlay/rental-overlay.component'
import { ImportCalendarDialogService } from '@tv3/containers/calendar/shared/import-calendar-dialog/import-calendar-dialog.service'
import { CalendarService } from '@tv3/store/calendar/calendar.service'
import { ShareCalendarDialogService } from '../shared/share-calendar-dialog/share-calendar-dialog.service'
import * as R from 'ramda'
import { AddBookingComplete, DeleteInquiryComplete } from '@tv3/store/inquiry/inquiry.actions'
import { AddImportedCalendarConnectionComplete } from '@tv3/store/connection/connection.actions'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { distinctObjectUntilChangedByFields } from '@tokeet-frontend/tv3-platform'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { RatesService } from '@tv3/containers/rates/rate-services/rates.service'
import { BookedDatesService } from '@tv3/store/calendar/booked-dates.service'
import { EditRateDialogInput } from '@tv3/containers/rates/edit-rate-dialog/edit-rate-dialog.component'
import { EditRateDialogService } from '@tv3/containers/rates/edit-rate-dialog/edit-rate-dialog.service'
import { AccountRatesDialogTab } from '@tv3/containers/rates/account-rates-dialog/account-rates-dialog.component'
import { AccountRatesDialogService } from '@tv3/containers/rates/account-rates-dialog/account-rates-dialog.service'
import { environment } from '@tv3/environments/environment'
import { HoldDialogService } from '../hold/hold-dialog/hold-dialog.service'
import { TableType } from '@tv3/shared/empty-table/table-type'
import { AddRentalDialogService } from '@tv3/containers/rentals/add/add-rental-dialog.service'
import { RentalRatesByCategory } from '../../rates/rate-services/rates.service'
import { BehaviorSubject, combineLatest, of, from } from 'rxjs'
import {
  GetAddEventActionParams,
  GetAddRateActionParams,
} from '../../../shared/dialogs/action-dialog/action-dialog.service'
import { AuthService } from '@tv3/services/auth.service'
import { HasPermission } from '@tokeet-frontend/permission'
import { calendarPlusMenu } from './calendar-plus-menu'
import { UniversalPlusMenuItem } from '@tv3/layout/navbar/global-plus-btn/universal-plus-menu'
import { BookingCostResolver } from '@tokeet/cost-resolver'
import { BookingEngineCost } from '@tv3/models/inquiry/booking-engine-cost'
import { tagsIntersection } from '@tokeet-frontend/tags'

@Component({
  selector: 'app-calendar-multi',
  templateUrl: './calendar-multi.component.html',
  styleUrls: ['../shared/calendar-component-common.scss', './calendar-multi.component.scss'],
  providers: [BookedDatesService],
})
export class CalendarMultiComponent extends Destroyable implements OnInit {
  // # region toolbar

  years: number[]
  months: number[]

  selectedYear: number
  selectedMonth: number

  lastNavigatingDate: number = null

  filters = this.filtersService.getMultiCalendarFilters()
  isFilteringIgnoreFields = ['start', 'end', 'availabilityType', 'initialDate', 'pageIndex', 'pageSize']

  filterAvailabilityDateRange: DateRangePickerSelection
  filterAvailabilityType: MultiCalendarAvailabilityTypes

  availabilityTypes = MultiCalendarAvailabilityTypes

  autoCloseDropdownFilters = true

  filtersCount = 0
  showDiscounts = environment.features.discounts

  // #endregion toolbar

  // # region calendar

  config: CalendarConfig

  tableTypes = TableType

  rentalsWithoutSubUnits: Rental[] = []
  rentalsById: Record<string, Rental> = {}
  events: Record<string, MultiCalendarEventView[]> = {}

  filteredRentals: Rental[] = []
  pageRentals: Rental[] = []
  filteredEvents: Record<string, MultiCalendarEventView[]> = {}

  expandedRentalId$ = new BehaviorSubject<string>(null)
  expendedSubunits: Rental[] = []
  private subunitNamesMap: { [multiUnitId: string]: string[] } = {}
  multiUnitGroups: Record<string, Rental[]> = {}

  filterTags: string[]
  filterChannels: string[]

  activePage = this.filters.get('pageIndex').value
  pageSize = this.filters.get('pageSize').value

  pageSizes = [5, 10, 20, 50, 100, 200, 500]
  pageRange = { from: 0, to: 0 }

  activeMonth: number
  loading = false

  @ViewChild('calendar', { static: false })
  calendar: CalendarComponent<Rental, MultiCalendarEventView>

  @ViewChild('paginator', { static: true })
  paginator: MatPaginator

  today = localTodayToUTC().unix()

  defaultRentalColor = DefaultRentalColor

  // # endregion calendar

  // # region tooltip

  currencySymbol: string

  // # endregion tooltip

  private uidFilterRentals: string = null

  calendarPlusMenu = lodash.cloneDeep(calendarPlusMenu)
  isPlusMenuOpen = false

  get currentUser() {
    return this.auth.user
  }

  canSeeCharges$ = new BehaviorSubject(false)

  protected readonly automatedRatesTabs = AccountRatesDialogTab

  constructor(
    private store: Store<fromRoot.State>,
    private actions$: Actions,
    private perfume: NgPerfume,
    private ngxRolesService: NgxRolesService,
    private filtersService: FiltersService,
    private inquirySharedDialogsService: InquirySharedDialogsService,
    private holdDialog: HoldDialogService,
    private shareCalendarDialog: ShareCalendarDialogService,
    private importCalendarDialog: ImportCalendarDialogService,
    private permissions: NgxPermissionsService,
    private auth: AuthService,
    private actionDialogService: ActionDialogService,
    private calendarService: CalendarService,
    private ratesService: RatesService,
    private amplitudeService: AmplitudeService,
    private bookedDatesService: BookedDatesService,
    private editRateDialogService: EditRateDialogService,
    private accountRatesDialogService: AccountRatesDialogService,
    private addRentalDialog: AddRentalDialogService
  ) {
    super()
    perfume.start('MultiCalendar')
  }

  ngOnInit() {
    this.amplitudeService.logEvent('calendar-view-multi')

    this.config = {
      sidebarWidth: 235,
      headerHeight: 60,
      cellWidth: 70,
      cellHeight: 60,
      eventSpanHeight: 60,
      // TV4-6455: filterRentals() paginates by top-level rentals, so the calendar gets one page of rows
      pageSize: Number.MAX_SAFE_INTEGER,
      showStrippedColumns: false,
      overlapMode: 'day',
      overlapThreshold: 1,
      trimEventHours: true,
      eventSpanAlign: 'start',
      selectionMode: 'multiple-row',
      isItemSelectable(item: Rental) {
        return isSubunit(item) ? !!item.multi_unit_status : true
      },
    }

    this.subscribeStore()
    this.subscribeFilters()

    this.prepareYears()

    this.store.pipe(select(selectAllRentalSubUnits), untilDestroy(this)).subscribe((subunits) => {
      const map: { [multiUnitId: string]: string[] } = {}
      subunits?.forEach((s) => {
        if (!s.multi_unit_id) return
        if (!map[s.multi_unit_id]) map[s.multi_unit_id] = []
        ;[s.name, s.nickname].filter(Boolean).forEach((n) => map[s.multi_unit_id].push(n.toLowerCase()))
      })
      this.subunitNamesMap = map
      this.filterRentals()
    })

    this.filterRentals()
  }

  // #region store

  private subscribeStore() {
    this.store
      .pipe(
        select(selectCalendarLoading),
        distinctUntilChanged(),
        tap((loading) => (this.loading = loading)),
        untilDestroy(this)
      )
      .subscribe()

    const watchRentalFields: (keyof Rental)[] = [
      'name',
      'status',
      'images',
      'bedrooms',
      'bathrooms',
      'multi_unit_status',
      'multi_unit_priority',
    ]
    this.expandedRentalId$
      .pipe(
        switchMap((id) =>
          combineLatest([
            this.store.pipe(
              select(selectAllRentals),
              map((rentals) => this.getRentalsForOwner(rentals)),
              distinctObjectUntilChangedByFields(watchRentalFields, 'id'),
              // only show double booked rentals if the filter is checked
              switchMap((items) =>
                this.filters.get('double').valueChanges.pipe(
                  startWith(this.filters.get('double').value),
                  switchMap((double) => {
                    if (!double) return of(items)
                    return this.store.pipe(
                      select(selectDoubleBookedCalendarEvents),
                      map((events) => events.map((e) => e.rentalId)),
                      map((ids) => items.filter((item) => ids.includes(item.id)))
                    )
                  })
                )
              )
            ),
            this.store.pipe(
              select(selectRentalSubUnits(id)),
              distinctObjectUntilChangedByFields(watchRentalFields, 'id')
            ),
          ])
        ),
        untilDestroy(this)
      )
      .subscribe(([rentalsWithoutSubUnits, subunits]: Rental[][]) => {
        this.expendedSubunits = subunits
        this.updateRentals(rentalsWithoutSubUnits)
        this.filterRentals()
      })

    combineLatest([
      this.store.pipe(
        select(selectMultiCalendarEventsViews),
        distinctUntilChanged(),
        tap((events) => {
          this.bookedDatesService.refresh(lodash.flatten(lodash.values(events)))
        })
      ),
      this.store.pipe(
        select(selectRentalMultiUnitGroups),
        tap((groups) => (this.multiUnitGroups = groups))
      ),
      this.store.pipe(
        select(selectRentalEntities),
        tap((rentals) => (this.rentalsById = rentals))
      ),
      this.expandedRentalId$.asObservable(),
    ])
      .pipe(untilDestroy(this))
      .subscribe(([events]) => {
        this.updateEvents(events)
      })

    this.actions$
      .pipe(
        ofType(LoadCalendarRangeComplete),
        tap((action) => {
          if (this.uidFilterRentals && action.uid === this.uidFilterRentals) {
            this.uidFilterRentals = null
            this.filterRentals()
          }
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(
          AddHoldEventComplete,
          UpdateHoldEventComplete,
          DeleteHoldEventComplete,
          AddImportedCalendarConnectionComplete,
          AddBookingComplete,
          DeleteInquiryComplete,
          ChangeInquiryArriveComplete,
          ChangeInquiryDepartComplete,
          ChangeInquiryGuestComplete,
          ConfirmBookingComplete,
          CancelBookingComplete,
          ChangeBookingDetailsComplete
        ),
        tap(() => {
          this.onViewportChange(this.calendar.activeViewportInfo)
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.actions$
      .pipe(
        ofType(updateRentalComplete, updateRentalBaseRateComplete),
        map((a) => a.update.id as string),
        untilDestroy(this)
      )
      .subscribe((rentalId) => {
        const viewportInfo = this.calendar.activeViewportInfo
        this.refreshRates([rentalId], viewportInfo.startDate, viewportInfo.endDate, true)
      })

    const canSeeCharges$ = () => {
      if (this.isOwner) {
        return of(!!this.currentUser?.settings?.total)
      } else {
        return from(this.permissions.hasPermission('canEditInquiry'))
      }
    }
    canSeeCharges$().subscribe((canSeeCharges) => {
      this.canSeeCharges$.next(canSeeCharges)
    })
  }

  private fetchEvents(from: number, to: number, uid: any = null) {
    this.store.dispatch(
      LoadCalendarRange({
        payload: { start: from, end: to },
        uid,
        reset: true,
      })
    )
  }

  // #endregion store

  // #region toolbar

  onRefresh() {
    this.store.dispatch(loadRentals({}))
    const viewportInfo = this.calendar.activeViewportInfo
    this.fetchEvents(viewportInfo.startDate, viewportInfo.endDate)
    this.fetchRates(viewportInfo.visibleItems, viewportInfo.startDate, viewportInfo.endDate, true)
  }

  navigate(type: 'prev' | 'next') {
    if (!isSomething(this.calendar)) {
      return
    }

    const viewportInfo = this.calendar.activeViewportInfo
    const days = toMoment(viewportInfo.endDate).diff(toMoment(viewportInfo.startDate), 'days')
    const date = toMoment(this.lastNavigatingDate || viewportInfo.startDate)

    if (type === 'next') {
      date.add(days, 'days')
    } else if (type === 'prev') {
      date.subtract(days, 'days')
    }

    this.lastNavigatingDate = date.startOf('date').unix()

    this.calendar.navigate(this.lastNavigatingDate)
  }

  onChangeYear = (type: 'prev' | 'next' = null) => {
    const index = this.years.indexOf(this.selectedYear)

    if (type === 'prev') {
      if (index === 0) return
      this.selectedYear = this.years[index - 1]
    } else if (type === 'next') {
      if (index === this.years.length - 1) return
      this.selectedYear = this.years[index + 1]
    }

    this.prepareMonths()
    this.onChangeMonth()
  }

  onChangeMonth = (type: 'prev' | 'next' = null) => {
    if (!this.calendar || !this.months || !this.months.length) return

    if (!type) {
      const monthIndex = this.months.indexOf(this.selectedMonth)
      const month = monthIndex !== -1 ? this.selectedMonth : lodash.first(this.months)
      if (isSomething(month)) {
        this.setCurrentMonth(month)
      }
      return
    }

    const yearIndex = this.years.indexOf(this.selectedYear)
    const index = this.months.indexOf(this.selectedMonth)

    if (index === -1) {
      const fallbackMonth = type === 'prev' ? lodash.last(this.months) : lodash.first(this.months)
      if (isSomething(fallbackMonth)) {
        this.setCurrentMonth(fallbackMonth)
      }
      return
    }

    if (type === 'prev') {
      if (index === 0) {
        if (yearIndex === 0) return

        this.selectedYear = this.years[yearIndex - 1]
        this.prepareMonths()

        this.setCurrentMonth(lodash.last(this.months))
      } else {
        this.setCurrentMonth(this.months[index - 1])
      }
    } else if (type === 'next') {
      if (index === this.months.length - 1) {
        if (yearIndex === this.years.length - 1) return

        this.selectedYear = this.years[yearIndex + 1]
        this.prepareMonths()

        this.setCurrentMonth(lodash.first(this.months))
      } else {
        this.setCurrentMonth(this.months[index + 1])
      }
    }
  }

  setCurrentMonth(month: number) {
    this.selectedMonth = month
    this.calendar?.navigate(month)
  }

  onChangePage(page: PageEvent) {
    this.activePage = page.pageIndex
    this.pageSize = page.pageSize
    this.filters.patchValue({ pageIndex: this.activePage, pageSize: this.pageSize })
    this.calendar?.sidebarScroller.scrollToOffset(0, 'auto')
    this.filterRentals()
  }

  onChangeFilterDateRange(range: DateRangePickerSelection) {
    this.filterAvailabilityDateRange = range

    this.filters.get('availabilityRange').setValue(
      range && {
        start: range.from,
        end: range.to,
      }
    )

    if (!range) {
      this.filterRentals()
      return
    }

    this.uidFilterRentals = lodash.uniqueId()
    this.fetchEvents(range.from, range.to, this.uidFilterRentals)

    this.calendar?.navigate(range.from)
  }

  onChangeFilterAvailabilityType(type: MultiCalendarAvailabilityTypes) {
    this.filterAvailabilityType = type

    this.filters.get('availabilityType').setValue(type)

    this.filterRentals()
  }

  onAddBooking(start?: number, end?: number, rentalId?: string) {
    this.inquirySharedDialogsService.openCreateBooking(start, end, rentalId)
  }

  onAddInquiry(start?: number, end?: number, rentalId?: string) {
    this.inquirySharedDialogsService.openAddInquiry({
      defaults: {
        arriveDate: start && asLocalDate(start),
        departDate: end && asLocalDate(end),
        rentalId,
      },
    })
  }

  onAddHoldEvent(start?: number, end?: number, rentalId?: string) {
    this.holdDialog.open({ defaults: { start, end, rentalIds: rentalId ? [rentalId] : [] } })
  }

  onImportCalendar() {
    this.importCalendarDialog.open()
  }

  onShareCalendar() {
    this.shareCalendarDialog.open()
  }

  gotoToday() {
    this.calendar.navigate(this.today)
  }

  onAddStandardRate(rentals: Rental[] = [], start: number = null, end: number = null) {
    this.openRateDialog({ type: 'standard', start, end }, rentals)
  }

  onAddDynamicRate(rentals: Rental[] = [], start: number = null, end: number = null) {
    this.openRateDialog({ type: 'dynamic', start, end }, rentals)
  }

  openRateDialog(data: EditRateDialogInput, rentals?: Rental[]) {
    const isGroupRate = R.defaultTo([], rentals).length > 1

    const dialogRef = this.editRateDialogService.open({
      rental: isGroupRate ? null : lodash.first(rentals) || null,
      defaultRentals: isGroupRate ? rentals : null,
      isGroupRate,
      ...data,
    })

    dialogRef.beforeClosed().subscribe((result) => {
      if (result && result.rate) this.onRefresh()
    })
  }

  openChannelAdjustments() {
    this.accountRatesDialogService.open({ activeTab: AccountRatesDialogTab.ChannelAutomations })
  }

  onAutomatedRates() {
    this.accountRatesDialogService.open()
  }

  openGroupRates() {
    this.accountRatesDialogService.open({ activeTab: AccountRatesDialogTab.GroupRates })
  }

  openLosDiscounts() {
    this.accountRatesDialogService.open({ activeTab: AccountRatesDialogTab.LosDiscounts })
  }

  openDiscountCodes() {
    this.accountRatesDialogService.open({ activeTab: AccountRatesDialogTab.DiscountCodes })
  }

  private prepareYears() {
    const year = toMoment().year()

    const range = 2

    const years = lodash.range(year - range, year + range + 1)

    this.years = years
    this.selectedYear = years[range]

    this.prepareMonths(true)

    const m = toMoment()

    const minDate = m.year(lodash.first(years)).startOf('year').unix()
    const maxDate = m.year(lodash.last(years)).endOf('year').unix()

    let initialDate = this.filters.get('initialDate').value

    if (!initialDate || initialDate < minDate || initialDate > maxDate) {
      initialDate = this.today
    }

    this.config = {
      ...this.config,
      minDate,
      maxDate,
      initialDate,
    }
  }

  private prepareMonths(init = false) {
    const today = toMoment(this.today)

    const year = moment.utc({ year: this.selectedYear })

    let selectedMonth: number

    const start = year.clone().startOf('year').unix()
    const end = year.clone().endOf('year').unix()

    const months = itemsInTimeRange(start, end, 'month')

    if (init && year.isSame(today, 'year')) {
      selectedMonth = today.startOf('month').unix()
    }

    this.months = months

    if (!selectedMonth) {
      selectedMonth = toMoment(this.selectedMonth || this.months[0])
        .year(this.selectedYear)
        .startOf('month')
        .unix()
    }
    if (init) {
      this.setCurrentMonth(selectedMonth)
    } else if (isSomething(selectedMonth)) {
      this.selectedMonth = selectedMonth
    }
  }

  // #endregion toolbar

  get isOwner() {
    return this.ngxRolesService.getRole('OWNER')
  }

  get search(): string {
    return this.filters.get('search').value || ''
  }

  //#region calendar

  getItemID(item: Rental) {
    return item.id
  }

  getEventID = (event: MultiCalendarEventView) => {
    return this.calendarService.getEventID(event)
  }

  getEventTimeRange = (event: MultiCalendarEventView): CalendarEventTimeRange => {
    return this.calendarService.getEventTimeRange(event)
  }

  onViewportChange(viewportInfo: CalendarViewPortInfo<Rental>) {
    this.calendar?.onContentScroll()

    this.filters.get('pageIndex').setValue(this.activePage)
    this.filters.get('pageSize').setValue(this.pageSize)

    const activeMonthMoment = toMoment(viewportInfo.activeMonth || this.selectedMonth || this.today).startOf('month')
    const year = activeMonthMoment.year()

    this.selectedMonth = activeMonthMoment.unix()

    if (year !== this.selectedYear) {
      this.selectedYear = year
      this.prepareMonths()
    }

    this.lastNavigatingDate = null

    this.pageRange.from = this.activePage * this.pageSize + 1
    this.pageRange.to = Math.min((this.activePage + 1) * this.pageSize, this.filteredRentals.length)
    if (this.pageRange.to === 0) this.pageRange.from = 0

    this.fetchEvents(viewportInfo.startDate, viewportInfo.endDate)
    this.fetchRates(viewportInfo.visibleItems, viewportInfo.startDate, viewportInfo.endDate)

    this.filters.get('initialDate').setValue(viewportInfo.startDate)
  }

  onSelectionChange(selectionInfo: CalendarSelectionInfo<Rental>) {
    const { items } = selectionInfo

    if (!items.length) {
      return
    }

    const start = selectionInfo.start
    const end = selectionInfo.end

    this.showSelectionDialog(items, start, end)
  }

  private showSelectionDialog(rentals: Rental[], start: number, end: number) {
    const hasSubunits = rentals.some((r) => isSubunit(r))
    const hasDisabledSubunits = rentals.some((r) => isSubunit(r) && !r.multi_unit_status)
    const actions: GetAddEventActionParams & GetAddRateActionParams = {
      addInquiry: {
        disabled: rentals.length > 1 || hasDisabledSubunits,
        onClick: () => {
          this.onAddInquiry(start, end, rentals[0]?.id)
        },
      },
      addBooking: {
        disabled: rentals.length > 1 || hasDisabledSubunits,
        onClick: () => {
          this.onAddBooking(start, end, rentals[0]?.id)
        },
      },
      addHoldEvent: {
        onClick: () => {
          this.holdDialog.open({ defaults: { start, end, rentalIds: rentals.map((r) => r.id) } })
        },
      },
      addDynamicRate: {
        disabled: hasSubunits,
        onClick: () => this.onAddDynamicRate(rentals, start, end),
      },
      addStandardRate: {
        disabled: hasSubunits,
        onClick: () => this.onAddStandardRate(rentals, start, end),
      },
    }
    if (hasSubunits) {
      actions.addDynamicRate.tooltip = $localize`Disabled for sub-units. Rates are inherited from the main unit.`
      actions.addStandardRate.tooltip = $localize`Disabled for sub-units. Rates are inherited from the main unit.`
    }
    this.actionDialogService
      .openCalendarActions(actions)
      .afterClosed()
      .subscribe(() => {
        this.calendar.clearSelections()
      })
  }

  onExpandMainUnit(rental: Rental) {
    this.expandedRentalId$.next(this.expandedRentalId$.value === rental.id ? null : rental.id)
  }

  filterRentals() {
    const search = this.search.trim().toLowerCase()
    const matchesSearch = (name: string) =>
      String(name || '')
        .toLowerCase()
        .includes(search)

    let rentals = [...this.rentalsWithoutSubUnits]

    if (search) {
      rentals = rentals.filter((rental) => {
        if (matchesSearch(rental.nickname || rental.name)) return true
        return (this.subunitNamesMap[rental.id] || []).some((n) => n.includes(search))
      })
    }

    const availabilityType = this.filterAvailabilityType
    const availabilityDateRange = this.filterAvailabilityDateRange

    const availabilityStart = availabilityDateRange && toMoment(availabilityDateRange.from).endOf('date').unix()
    const availabilityEnd = availabilityDateRange && toMoment(availabilityDateRange.to).startOf('date').unix()

    if (availabilityDateRange && availabilityType === MultiCalendarAvailabilityTypes.available) {
      rentals = rentals.filter((rental) => {
        const events = this.filteredEvents[rental.id]
        let available = true

        lodash.forEach(events, (event) => {
          if (event.end < availabilityStart || event.start > availabilityEnd) return
          available = false
          return false
        })

        return available
      })
    }

    if (availabilityDateRange && availabilityType === MultiCalendarAvailabilityTypes.booked) {
      rentals = rentals.filter((rental) => {
        const events = this.filteredEvents[rental.id]
        let booked = false

        lodash.forEach(events, (event) => {
          if (event.end < availabilityStart || event.start > availabilityEnd) return

          booked = true
          return false
        })

        return booked
      })
    }

    const { filterRentals, filterTags, filterTaggedRentalIds } = this.updateFilters()

    rentals = rentals.filter((rental) => {
      if (filterRentals.length && !filterRentals.includes(rental.id)) return false

      if (filterTags.length || filterTaggedRentalIds.length) {
        if (!filterTaggedRentalIds.includes(rental.id) && !tagsIntersection(filterTags, rental.tags || []).length)
          return false
      }

      return true
    })

    this.filteredRentals = rentals

    const lastPage = Math.max(0, Math.ceil(rentals.length / this.pageSize) - 1)
    this.activePage = Math.min(this.activePage, lastPage)
    const pageStart = this.activePage * this.pageSize
    this.pageRentals = rentals.slice(pageStart, pageStart + this.pageSize)

    // insert expended subunits on the main unit's page
    if (this.expendedSubunits.length) {
      const mainUnitIndex = this.pageRentals.findIndex((r) => r.id === this.expandedRentalId$.value)
      if (mainUnitIndex > -1) {
        const mainUnit = this.pageRentals[mainUnitIndex]
        // a search that matches the main unit's own name shows all of its subunits
        const visibleSubunits =
          search && !matchesSearch(mainUnit.nickname || mainUnit.name)
            ? this.expendedSubunits.filter((s) => matchesSearch(s.name) || matchesSearch(s.nickname))
            : this.expendedSubunits
        this.pageRentals = [
          ...this.pageRentals.slice(0, mainUnitIndex + 1),
          ...visibleSubunits,
          ...this.pageRentals.slice(mainUnitIndex + 1),
        ]
      }
    }
  }

  private updateRentals(rentalsWithoutSubUnits: Rental[]) {
    this.rentalsWithoutSubUnits = rentalsWithoutSubUnits

    this.filterTags = R.pipe(
      R.map((r: Rental) => R.defaultTo([], r.tags)),
      R.flatten,
      R.uniq,
      R.reject(R.isNil),
      R.map(R.pipe(R.when(R.is(Number), R.toString), R.toLower)),
      R.sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    )(this.rentalsWithoutSubUnits)
  }

  private getRentalsForOwner(allRentals: Rental[]): Rental[] {
    // If user can see all rentals, use standard behavior (exclude sub-units)
    if (this.permissions.getPermission('canSeeAllRentals')) {
      return allRentals.filter((t) => !t.multi_unit_id || t.id === t.multi_unit_id)
    }

    // Filter to only owned rentals
    const userId = this.currentUser?.id
    const ownedRentals = allRentals.filter((r) => (r.owners || []).includes(userId))

    // Get owned main units
    const ownedMainUnits = ownedRentals.filter((t) => !t.multi_unit_id || t.id === t.multi_unit_id)

    // Get owned sub-units whose main unit is NOT owned (orphaned sub-units)
    const orphanedSubunits = ownedRentals.filter(
      (r) => r.multi_unit_id && r.id !== r.multi_unit_id && !ownedRentals.some((main) => main.id === r.multi_unit_id)
    )

    return [...ownedMainUnits, ...orphanedSubunits]
  }

  private filterEvents() {
    const { filterChannels } = this.updateFilters()

    if (filterChannels.length) {
      this.filteredEvents = lodash.mapValues(this.events, (events) => {
        return events.filter((event) => filterChannels.includes(event.source))
      })
    } else {
      this.filteredEvents = this.events
    }
    if (!lodash.isEmpty(this.filteredEvents)) {
      this.perfume.end('MultiCalendar')
    }

    this.filterRentals()
  }

  private updateEvents(events: Record<string, MultiCalendarEventView[]>) {
    events = lodash.reduce(
      events,
      (acc, rentalEvents, rentalId) => {
        acc[rentalId] = rentalEvents
        return acc
      },
      {} as Record<string, MultiCalendarEventView[]>
    )

    this.events = events

    this.filterChannels = lodash
      .chain(events)
      .map((itemEvents) => itemEvents.map((event) => event.source))
      .flatten()
      .compact()
      .uniq()
      .sortBy((source) => source.toLowerCase())
      .value()

    // clear not existing channels
    const chosenChannels = this.filters.get('channels').value
    const existingChannels = R.filter((item) => R.contains(item, this.filterChannels), chosenChannels || [])
    if (chosenChannels.length !== existingChannels.length) {
      this.filters.patchValue({ channels: existingChannels })
    }

    this.store
      .pipe(
        select(selectRentalsLoaded),
        filter((loaded) => loaded),
        take(1)
      )
      .subscribe(() => {
        // clear not existing rentals
        const chosenRentals = this.filters.get('rentals').value
        const existingRentals = R.filter(
          (item) =>
            R.contains(
              item,
              R.map((r: Rental) => r.id, this.rentalsWithoutSubUnits)
            ),
          chosenRentals || []
        )
        if (chosenRentals.length !== existingRentals.length) {
          this.filters.patchValue({ rentals: existingRentals })
        }
      })

    this.filterEvents()
  }

  private updateFilters() {
    const filterRentals = (this.filters.get('rentals').value || []) as string[]
    const filterTags = (this.filters.get('tags').value || []) as string[]
    const filterTaggedRentalIds = (this.filters.get('taggedIds').value || []) as string[]
    const filterChannels = (this.filters.get('channels').value || []) as string[]

    this.filtersCount =
      filterRentals.length +
      filterChannels.length +
      filterTags.length +
      filterTaggedRentalIds.length +
      (this.filters.get('hideRates').value ? 1 : 0)

    return { filterRentals, filterTags, filterChannels, filterTaggedRentalIds }
  }

  clearFilters() {
    const defaultFilters = this.filtersService.defaultFilters[FilterGroup.MultiCalendar]

    this.filters.setValue(defaultFilters)
    this.populateFilters()

    this.gotoToday()
  }

  private subscribeFilters() {
    this.populateFilters()

    const filterRentalsProps = ['rentals', 'tags', 'taggedIds', 'search']
    const filterEventsProps = ['channels']

    const resetPageProps = [...filterRentalsProps, 'availabilityType', 'availabilityRange']

    resetPageProps.forEach((filterProp) => {
      this.filters
        .get(filterProp)
        .valueChanges.pipe(
          distinctUntilChanged(),
          tap(() => this.paginator.firstPage()),
          untilDestroy(this)
        )
        .subscribe()
    })

    filterRentalsProps.forEach((filterProp) => {
      this.filters
        .get(filterProp)
        .valueChanges.pipe(
          distinctUntilChanged(),
          tap(() => this.filterRentals()),
          untilDestroy(this)
        )
        .subscribe()
    })

    filterEventsProps.forEach((filterProp) => {
      this.filters
        .get(filterProp)
        .valueChanges.pipe(
          distinctUntilChanged(),
          tap(() => this.filterEvents()),
          untilDestroy(this)
        )
        .subscribe()
    })

    this.filters.valueChanges
      .pipe(
        tap(() => this.updateFilters()),
        debounceTime(1000),
        tap((values) => {
          this.filtersService.storeMultiCalendar(values)
        }),
        untilDestroy(this)
      )
      .subscribe()
  }

  private populateFilters() {
    // TV3-1201: availability type should be "available" by default
    this.filterAvailabilityType = MultiCalendarAvailabilityTypes.available

    const availabilityRange = this.filters.get('availabilityRange').value

    this.onChangeFilterDateRange(
      availabilityRange && {
        from: availabilityRange.start,
        to: availabilityRange.end,
      }
    )
  }

  isBooked(rentalId: string, date: number) {
    return this.bookedDatesService.isBooked(rentalId, date)
  }

  getBookedOnAvailableUnits(rentalId: string, date: number) {
    const subunits = this.multiUnitGroups[rentalId] || []

    const enabledSubunits = subunits?.filter((subunit) => !!subunit.multi_unit_status)
    const bookedSubunits = enabledSubunits?.filter((subunit) => !!this.isBooked(subunit.id, date))

    return `${bookedSubunits.length}/${enabledSubunits.length}`
  }

  getAvailableUnits(rentalId: string, date: number) {
    const subunits = this.multiUnitGroups[rentalId] || []
    return subunits?.filter((subunit) => !!subunit.multi_unit_status && !this.isBooked(subunit.id, date))
  }

  //#endregion calendar

  //#region rates

  rentalRatesByCategory: RentalRatesByCategory = {}
  getDefaultRate = (data: RentalRatesByCategory, rental: Rental, date: number) => {
    const rentalId = isSubunit(rental) ? rental.multi_unit_id : rental.id
    return data?.[rentalId]?.['default']?.[date] || data?.[rentalId]?.[TOKEET_CHANNEL_ID]?.[date]
  }

  getRentalCategoryRates(rental: Rental, date: number) {
    const rentalId = isSubunit(rental) ? rental.multi_unit_id : rental.id
    const data = this.rentalRatesByCategory?.[rentalId]
    return lodash.mapValues(data, (t) => t[date])
  }

  private fetchRates(rentals: Rental[], start: number, end: number, refresh = false) {
    const rentalIds = rentals.map((rental) => rental.id)
    this.refreshRates(rentalIds, start, end, refresh)
  }

  private refreshRates(rentalIds: string[], start: number, end: number, refresh = false) {
    this.ratesService.fetchRatesV2(rentalIds, start, end, refresh).subscribe((data) => {
      this.rentalRatesByCategory = data
    })
  }

  // #endregion rates

  getBookingEventCost(event: MultiCalendarEventView) {
    const cost = BookingCostResolver.parseBookingCharges({
      ...event,
      cost: event.cost,
      inquirySource: event.source,
      booked: 1,
      bookingEngine: event.booking_engine ? BookingEngineCost.deserialize(event.booking_engine) : null,
    } as any)
    return cost?.sum || 0
  }

  showEventDetails(event: MultiCalendarEventView) {
    if (event.isHoldEvent) {
      this.onEditHold(event)
    }
  }

  onEditHold(event: MultiCalendarEventView) {
    if (this.permissions.getPermission('canEditHold') && event.isHoldEvent) {
      this.holdDialog.open({ hold: event, convertable: true, deletable: true })
    }
  }

  showRentalPopover(sidebarCell: HTMLElement, popover: NgbPopover) {
    const span = sidebarCell.querySelector('span')
    const isOverflow = span && span.scrollWidth > span.clientWidth

    if (isOverflow) popover.open()
  }

  @HasPermission('canEditRental')
  onOpenRental(rental: Rental) {
    this.store.dispatch(OpenRentalOverlay({ rental, activeTab: RentalDialogTab.Calendar }))
  }

  onAddPrice(tab: AccountRatesDialogTab = AccountRatesDialogTab.ChannelAutomations) {
    this.accountRatesDialogService.open({ activeTab: tab })
  }

  @HasPermission('canEditRental')
  onAddRental() {
    this.addRentalDialog.open().subscribe()
  }

  onMouseoverMainPlusMenuItem(item: UniversalPlusMenuItem, index: number) {
    item.open = true
    this.calendarPlusMenu.forEach((menuItem, i) => {
      if (i !== index) {
        menuItem.open = false
      }
    })
  }

  onMenuItemClick(item: UniversalPlusMenuItem) {
    if (item.action) {
      this.store.dispatch(item.action)
    }
  }
}
